import axios from "axios";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import {
  COURTESY_PRODUCT_OPTIONS as PRODUCT_OPTIONS,
  COURTESY_JUSSUB_ALLOWED_DAYS,
  COURTESY_JUSSUB_DURATION_OPTIONS,
  COURTESY_MIN_DAYS,
  COURTESY_MAX_DAYS
} from "../../../constants/courtesy";
import { PlansAPI } from "../../../api/payments/plans/api";

export default function CourtesyModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  clientEmail,
  currentPlanName,
  useJusSub
}) {
  const [product, setProduct] = useState(null);
  const [duration, setDuration] = useState("");
  const [planMode, setPlanMode] = useState(useJusSub ? "other" : "same");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const resetFields = () => {
    setProduct(null);
    setDuration("");
    setPlanMode(useJusSub ? "other" : "same");
    setSelectedPlan(null);
  };

  useEffect(() => {
    if (isOpen) {
      resetFields();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;

    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);

        if (useJusSub) {
          const response = await PlansAPI.list();
          const plansList = response.data?.content || [];
          if (!cancelled) {
            setPlans(plansList);
            setFilteredPlans(plansList.slice(0, 10));
          }
          return;
        }

        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/subscription/plans_admin"
        );
        if (!cancelled && response.data?.plans) {
          setPlans(response.data.plans);
          setFilteredPlans(response.data.plans.slice(0, 10));
        }
      } catch (err) {
        // no fluxo JusSub o erro já é exibido via toast pela camada de API
        if (!cancelled && !useJusSub) {
          toast.error("Ocorreu um erro ao carregar os planos.");
        }
      } finally {
        if (!cancelled) setIsLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [isOpen, useJusSub]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilter = useCallback(
    debounce(inputValue => {
      if (inputValue) {
        setFilteredPlans(
          plans.filter(plan =>
            plan.name.toLowerCase().includes(inputValue.toLowerCase())
          )
        );
      } else {
        setFilteredPlans(plans.slice(0, 10));
      }
    }, 300),
    [plans]
  );

  const handleConfirm = async () => {
    if (!product?.value) {
      toast.error("Por favor, selecione o tipo de liberação da cortesia.");
      return;
    }

    if ((planMode === "other" || useJusSub) && !selectedPlan?.id) {
      toast.error("Por favor, selecione o plano.");
      return;
    }

    const durationValue = Number(duration);
    const hasDuration = duration !== "";
    const isValidJusSubDuration = COURTESY_JUSSUB_ALLOWED_DAYS.includes(
      durationValue
    );
    // vazio = duração padrão do plano
    const isValidJusbillDuration =
      !hasDuration ||
      (Number.isInteger(durationValue) &&
        durationValue >= COURTESY_MIN_DAYS &&
        durationValue <= COURTESY_MAX_DAYS);

    if (useJusSub && !isValidJusSubDuration) {
      toast.error("Por favor, selecione a duração da cortesia.");
      return;
    }

    if (!useJusSub && !isValidJusbillDuration) {
      toast.error(
        `A duração deve ser um número inteiro entre ${COURTESY_MIN_DAYS} e ${COURTESY_MAX_DAYS} dias.`
      );
      return;
    }

    const data = {
      ReleaseCategory: product.value.trim().toLowerCase()
    };

    if (hasDuration) {
      data.activeDays = durationValue;
    }

    if (planMode === "other" || useJusSub) {
      data.planId = String(selectedPlan.id);
    }

    if (onConfirm) {
      await onConfirm(data);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header className="border-bottom">
        <Modal.Title className="text-lg font-semibold text-gray-900">
          Criar cortesia
        </Modal.Title>
        <button type="button" className="close" onClick={onClose}>
          <i aria-hidden="true" className="ki ki-close"></i>
        </button>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-4">
          O cliente receberá acesso de cortesia ao produto selecionado.
        </p>

        <div className="mb-4">
          <label className="form-label">E-mail do cliente</label>
          <input disabled className="form-control" value={clientEmail || ""} />
        </div>

        <div className="mb-4">
          <label className="form-label">
            Tipo de liberação <span className="text-danger">*</span>
          </label>
          <CreatableSelect
            className="form-select"
            placeholder="Selecione ou digite o tipo de liberação"
            options={PRODUCT_OPTIONS}
            value={product}
            formatCreateLabel={inputValue => `Usar "${inputValue}"`}
            onChange={setProduct}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">Plano</label>
          <div className="radio-list">
            {!useJusSub && (
              <>
                <label className="radio">
                  <input
                    type="radio"
                    name="planMode"
                    checked={planMode === "same"}
                    onChange={() => setPlanMode("same")}
                  />
                  <span></span>
                  Mesmo plano do cliente{currentPlanName ? ` (${currentPlanName})` : ""}
                </label>
              <label className="radio">
                <input
                  type="radio"
                  name="planMode"
                  checked={planMode === "other"}
                  onChange={() => setPlanMode("other")}
                />
                <span></span>
                Selecionar outro plano
              </label>
              </>
            )}
          </div>
          {(planMode === "other" || useJusSub) && (
            <Select
              options={filteredPlans}
              value={selectedPlan}
              isLoading={isLoadingPlans}
              onChange={setSelectedPlan}
              onInputChange={debouncedFilter}
              placeholder="Selecione o plano.."
              getOptionLabel={option => option.name}
              getOptionValue={option => option.id}
              noOptionsMessage={() => <span>Sem resultados.</span>}
            />
          )}
        </div>

        <div className="mb-4">
          <label className="form-label">
            Duração{useJusSub ? "" : " (dias)"}
            {useJusSub && <span className="text-danger"> *</span>}
          </label>
          {useJusSub ? (
            <select
              className="form-control"
              value={duration}
              onChange={e => setDuration(e.target.value)}
            >
              <option value="" disabled>
                Selecione a duração
              </option>
              {COURTESY_JUSSUB_DURATION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                className="form-control"
                type="number"
                min={COURTESY_MIN_DAYS}
                max={COURTESY_MAX_DAYS}
                step="1"
                value={duration}
                onChange={e => setDuration(e.currentTarget.value)}
                placeholder="Padrão do plano"
              />
              <small className="form-text text-muted">
                Deixe em branco para usar a duração padrão do plano, ou
                informe de {COURTESY_MIN_DAYS} a {COURTESY_MAX_DAYS} dias.
              </small>
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? "Criando..." : "Criar cortesia"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
